import { useEffect, useMemo, useState } from 'react';
import * as adminService from '@/services/adminService';

const typeLabels = {
  gps_spoof: 'GPS suspeito',
  duplicate_collect: 'Coleta duplicada',
  rapid_collect: 'Coleta rápida',
  qr_replay: 'QR repetido',
  impossible_speed: 'Velocidade impossível',
};

const severityLabels = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
};

const severityStyles = {
  low: 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light',
  medium: 'border-accent/30 bg-accent/10 text-accent',
  high: 'border-energy/30 bg-energy/10 text-energy',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-PT');
};

const formatDetails = (details) => {
  if (!details) return 'Sem detalhes';
  if (typeof details === 'string') return details;
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return 'Detalhes indisponíveis';
  }
};

export default function AdminFraudLogsPage() {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const stats = useMemo(() => ({
    total: logs.length,
    open: logs.filter((log) => !log.resolvedAt).length,
    high: logs.filter((log) => log.severity === 'high').length,
    resolved: logs.filter((log) => log.resolvedAt).length,
  }), [logs]);

  const filteredLogs = useMemo(() => {
    if (filter === 'all') return logs;
    if (filter === 'open') return logs.filter((log) => !log.resolvedAt);
    if (filter === 'resolved') return logs.filter((log) => log.resolvedAt);
    return logs.filter((log) => log.severity === filter);
  }, [filter, logs]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminService.getFraudLogs({ limit: 100 });
      setLogs(res.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar os alertas de fraude.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Segurança</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Monitor de Fraude</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Analisa eventos suspeitos de GPS, coletas e códigos QR para proteger a economia do jogo.
          </p>
        </div>
        <button type="button" onClick={loadData} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Atualizar
        </button>
      </div>

      {error && (
        <div className="mt-5 rounded-lg border border-energy/40 bg-energy/10 p-4 text-sm font-bold text-energy">{error}</div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Total', stats.total],
          ['Abertos', stats.open],
          ['Alta gravidade', stats.high],
          ['Resolvidos', stats.resolved],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Alertas registados</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'open', label: 'Abertos' },
              { value: 'all', label: 'Todos' },
              { value: 'high', label: 'Alta' },
              { value: 'medium', label: 'Média' },
              { value: 'low', label: 'Baixa' },
              { value: 'resolved', label: 'Resolvidos' },
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === item.value ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Jogador</th>
                <th className="px-4 py-3">Severidade</th>
                <th className="px-4 py-3">Origem</th>
                <th className="px-4 py-3">Detalhes</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && filteredLogs.length === 0 && (
                <tr><td className="px-4 py-6 text-fg/45" colSpan="7">Nenhum alerta encontrado.</td></tr>
              )}
              {filteredLogs.map((log) => (
                <tr key={log.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{typeLabels[log.type] || log.type}</div>
                    <div className="mt-1 max-w-[200px] truncate font-mono text-xs text-fg/30">{log.id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg/80">{log.player?.displayName || log.player?.username || '-'}</div>
                    <div className="mt-1 text-xs text-fg/35">{log.user?.email || log.player?.username || 'Sem utilizador'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${severityStyles[log.severity] || severityStyles.low}`}>
                      {severityLabels[log.severity] || log.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg/60">
                    {log.ipAddress || '-'}
                    <div className="mt-1 max-w-[180px] truncate text-xs text-fg/35">{log.userAgent || 'Sem user agent'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <pre className="max-h-24 max-w-[300px] overflow-auto whitespace-pre-wrap rounded-lg border border-fg/10 bg-black/20 p-2 text-xs leading-relaxed text-fg/55">
                      {formatDetails(log.details)}
                    </pre>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${log.resolvedAt ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-accent/30 bg-accent/10 text-accent'}`}>
                      {log.resolvedAt ? 'Resolvido' : 'Aberto'}
                    </span>
                    {log.resolvedAt && <div className="mt-1 text-xs text-fg/35">{formatDate(log.resolvedAt)}</div>}
                  </td>
                  <td className="px-4 py-3 text-fg/60">{formatDate(log.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
